import * as React from "react";
import { Info, OctagonX, TriangleAlert } from "lucide-react";
import { SEVERITY_META, type SeverityTier } from "@taskiq/shared";
import { cn } from "@/lib/cn";

/**
 * Small status pills. Appearance is `.badge` in `styles/ui.css`.
 *
 * `SeverityChip` is the one that matters, and it exists as its own component
 * rather than a `Badge tone="stop"` for a reason: PRD §10 and basic
 * accessibility both require that severity is **never carried by colour alone**.
 * Roughly 8% of this overwhelmingly male workforce has a red/green deficiency,
 * and a hazard list they cannot read is worse than no hazard list.
 *
 * So every severity is rendered as colour + icon + text label, always, with no
 * prop to turn any of the three off.
 */

type Tone = "neutral" | "accent" | "advisory" | "caution" | "stop" | "ok";
type Size = "sm" | "md";

export function Badge({
  tone = "neutral",
  size = "md",
  uppercase = false,
  icon,
  children,
  className,
}: {
  tone?: Tone;
  size?: Size;
  uppercase?: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "badge",
        `badge--${tone}`,
        `badge--${size}`,
        uppercase && "badge--uppercase",
        className
      )}
    >
      {icon}
      {children}
    </span>
  );
}

export const SEVERITY_ICONS: Record<SeverityTier, React.ComponentType<{ className?: string }>> = {
  advisory: Info,
  caution: TriangleAlert,
  stop: OctagonX,
};

export function SeverityChip({
  severity,
  size = "md",
  className,
}: {
  severity: SeverityTier;
  /** `sm` where the chip is supporting a line rather than being the line. */
  size?: Size;
  className?: string;
}) {
  const meta = SEVERITY_META[severity];
  const Icon = SEVERITY_ICONS[severity];

  return (
    <Badge tone={severity} size={size} icon={<Icon />} className={className}>
      {meta.label}
    </Badge>
  );
}
